import moment from 'moment'
import CheckIcon from '@mui/icons-material/Check'
import tables, {INITIAL_PAGE_SIZE} from 'constants/tables'
import {familyStatuses, genderKod} from 'constants/users'
import RequestStatus from 'components/Display/Table/CellComponents/Status'
import Report from 'components/Display/Table/CellComponents/Report'
import OpenRow from 'components/Display/Table/CellComponents/OpenRow'
import {removeUserPrefixes} from './apiUtils'
import {Chip} from '@mui/material'
import HistoryRequestStatus from 'components/Display/Table/CellComponents/HistoryRequestStatus'

const date = (value) => value && moment(value).format('D/M/YYYY')
const datetime = (value) => value && moment(value).format('D/M/YYYY HH:mm')
const number = (value) => value?.toFixed(2)
const boolean = (value) => (value ? <CheckIcon /> : null)
const email = (value) => <a href={`mailto:${value.trim()}`}>{value.trim()}</a>
const url = (value) => (
    <a href={value?.trim()} target="_blank" rel="noopener noreferrer">
        {value}
    </a>
)
const gender = (value, i18n) => {
    if (!value) {
        return ''
    }
    const val = genderKod[value] || value
    return i18n.t(`users.GenderKods.${val}`)
}
const familyStatus = (value, i18n) => {
    if (!value) {
        return ''
    }
    const val = familyStatuses[value] || value
    return i18n.t(`users.FamilyStatuss.${val}`)
}
const requestStatus = (value) => <RequestStatus value={value} />
const historyRequestStatus = (value) => <HistoryRequestStatus value={value} />
const report = (rowData) => () => <Report rowData={rowData} />
const clientName = (rowData) => () =>
    (
        <OpenRow
            value={`${rowData.ClientFirstName} ${rowData.ClientLastName}`}
            rowData={rowData}
        />
    )

const priority = (value) => {
    return (
        value && (
            <Chip
                color="default"
                variant="outlined"
                sx={{br: 2, height: 25}}
                label={value}
            />
        )
    )
}

export const formatKeyFunctionsByType = (rowData) => ({
    date,
    datetime,
    number,
    boolean,
    email,
    url,
    gender,
    familyStatus,
    requestStatus,
    report: report(rowData),
    clientName: clientName(rowData),
    removeClientPrefix: removeUserPrefixes(rowData?.AccountKey),
    priority,
    historyRequestStatus,
})
export const getItemId = (item, idKey) =>
    item._id || item.id || item.key || item[idKey]

const extractHeaderKeys = (data) =>
    data.length > 0 ? Object.keys(data[0]) : []

const sortByItemKey = (itemKey, direction) => (item1, item2) => {
    const equality =
        direction === 'asc'
            ? item1[itemKey] < item2[itemKey]
            : item1[itemKey] > item2[itemKey]

    return equality ? -1 : 1
}

const getParsedSort = (sort) => {
    if (sort) {
        return JSON.parse(sort)
    }
    return {}
}

export const getSortDirection = (itemKey, sort) => {
    const parsedSort = getParsedSort(sort)
    return parsedSort[itemKey]
}

export const setSortDirection = (itemName, itemKey, direction, setUrlParam) => {
    const parsedSort = {[itemKey]: direction}
    setUrlParam(`${itemName}_sort`, JSON.stringify(parsedSort))
}

const getVisibleData = (visibleKeys, data) => {
    if (visibleKeys) {
        return data?.map((row) => {
            return visibleKeys.reduce((accu, key) => {
                if (key === 'role' && row?.account?.role) {
                    accu[key] = row.account.role
                } else {
                    accu[key] = row[key]
                }
                return accu
            }, {})
        })
    }

    return data
}

const getSortedData = (sort, searchedData) => {
    const parsedSort = getParsedSort(sort)
    const sortKey = Object.keys(parsedSort)[0]

    let sortedData = [...searchedData]
    if (sortKey) {
        sortedData.sort(sortByItemKey(sortKey, parsedSort[sortKey]))
    }

    return sortedData
}

const extractTableData = (
    data,
    visibleKeys,
    page,
    pageSize,
    search,
    sort,
    paginationType
) => {
    page = Number(page || 0)
    pageSize = Number(pageSize || INITIAL_PAGE_SIZE)
    if (paginationType === 'local') {
        data = data.slice(page * pageSize, (page + 1) * pageSize)
    }

    const visibleData = getVisibleData(visibleKeys, data)

    if (paginationType === 'external') {
        return visibleData
    }

    const searchedData = visibleData?.filter((row) =>
        isSearchedRow(row, search)
    )

    return getSortedData(sort, searchedData)
}

export const isSearchedRow = (row, search) =>
    !search ||
    Object.values(row).some((value) =>
        value?.toString().toLowerCase().includes(search.toLowerCase())
    )

const getTablekeys = (isMobile, name, visibleKeys, headerKeys) =>
    (isMobile && tables[name]?.visibleKeysMobile) ||
    tables[name]?.visibleKeys ||
    visibleKeys ||
    headerKeys

export const getTableData = (
    name,
    visibleKeys,
    data,
    page,
    pageSize,
    search,
    sort,
    paginationType,
    isMobile
) => {
    let tableItems = []
    let totalItems
    if (Array.isArray(data)) {
        tableItems = data
        totalItems = data.length
    } else if (data?.data) {
        tableItems = data.data
        totalItems = data.total
    }

    const headerKeys = extractHeaderKeys(tableItems)
    const tableKeys = getTablekeys(isMobile, name, visibleKeys, headerKeys)
    const tableData = extractTableData(
        tableItems,
        visibleKeys,
        page,
        pageSize,
        search,
        sort,
        paginationType
    )
    const keyTypes = tables[name]?.keyTypes || {}

    return {tableKeys, tableData, keyTypes, tableItems, totalItems}
}
